import React from "react";
import {
    Archive16Regular,
    ArrowUndo16Regular,
    FolderArrowRight16Regular,
    LinkSquare16Regular,
    Mail16Regular,
    MailRead16Regular,
    Open16Regular,
    PersonAdd16Regular,
    TaskListSquareLtr16Regular,
} from "@fluentui/react-icons";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { aktivesFenster, alleFenster } from "../windowManager/browserFenster/fensterRegister";
import { INK, LINE } from "../chat/chatTheme";
import type { DvagPostZeile } from "../../services/dvagPostService";

/** Ein offenes Menü: Eintrag + Mausposition (Viewport). */
export interface DvagPostMenue {
    x: number;
    y: number;
    zeile: DvagPostZeile;
}

interface Props {
    menue: DvagPostMenue | null;
    /** Für den Eintrag läuft schon eine Aktion — dann ist alles gesperrt. */
    beschaeftigt?: boolean;
    onNeuerVorgang: () => void;
    onVorgangZuordnen: () => void;
    onVorgangOeffnen: () => void;
    onKontaktZuweisen: () => void;
    onBeimKundenAblegen: () => void;
    onArchivUmschalten: () => void;
    onGelesenUmschalten: () => void;
    onClose: () => void;
}

const klemme = (x: number, y: number, w: number, h: number) => {
    const rand = 8;
    return {
        left: Math.max(rand, Math.min(x, aktivesFenster().innerWidth - w - rand)),
        top: Math.max(rand, Math.min(y, aktivesFenster().innerHeight - h - rand)),
    };
};

const punktStil: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "8px 10px",
    border: "none",
    background: "transparent",
    color: "inherit",
    cursor: "pointer",
    textAlign: "left",
    borderRadius: 6,
    fontSize: 13,
    lineHeight: "18px",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
};

const Trenner: React.FC = () => <div style={{ height: 1, background: LINE, margin: "4px 6px" }} />;

/**
 * Rechtsklick auf einen Eintrag der Kontakthistorie (T-293, 28.10.2026): dieselben
 * Wege wie das Kontextmenü der Mails (`MailContextMenu`), soweit es sie für einen
 * DVAG-Eintrag gibt. „Verschieben" heißt hier Posteingang ↔ Archiv — ein Eintrag
 * liegt in keinem Postfach-Ordner.
 *
 * Als Portal an der Mausposition, mit Klemmung an den Fensterrand; Klick daneben,
 * Escape, Scrollen und Größenänderung schließen es. Konkrete Farben statt
 * Fluent-Tokens: das Portal hängt außerhalb des FluentProviders.
 */
export const DvagPostKontextMenue: React.FC<Props> = ({
    menue,
    beschaeftigt,
    onNeuerVorgang,
    onVorgangZuordnen,
    onVorgangOeffnen,
    onKontaktZuweisen,
    onBeimKundenAblegen,
    onArchivUmschalten,
    onGelesenUmschalten,
    onClose,
}) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

    React.useLayoutEffect(() => {
        if (!menue) {
            setPos(null);
            return;
        }
        const r = ref.current?.getBoundingClientRect();
        setPos(klemme(menue.x, menue.y, r?.width ?? 0, r?.height ?? 0));
    }, [menue]);

    React.useEffect(() => {
        if (!menue) return;
        const daneben = (e: Event) => {
            if (ref.current?.contains(e.target as Node)) return;
            onClose();
        };
        const taste = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        alleFenster.addEventListener("pointerdown", daneben);
        alleFenster.addEventListener("keydown", taste);
        alleFenster.addEventListener("resize", onClose);
        alleFenster.addEventListener("scroll", daneben, true);
        return () => {
            alleFenster.removeEventListener("pointerdown", daneben);
            alleFenster.removeEventListener("keydown", taste);
            alleFenster.removeEventListener("resize", onClose);
            alleFenster.removeEventListener("scroll", daneben, true);
        };
    }, [menue, onClose]);

    if (!menue) return null;
    const z = menue.zeile;
    const archiviert = z.archiviertAm != null;

    const punkt = (titel: string, icon: React.ReactNode, aktion: () => void) => (
        <button
            key={titel}
            type="button"
            role="menuitem"
            style={{ ...punktStil, opacity: beschaeftigt ? 0.5 : 1, cursor: beschaeftigt ? "default" : "pointer" }}
            disabled={beschaeftigt}
            aria-disabled={beschaeftigt || undefined}
            onMouseEnter={(e) => (e.currentTarget.style.background = beschaeftigt ? "transparent" : "#f4eff6")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            onClick={() => {
                aktion();
                onClose();
            }}
        >
            {icon}
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{titel}</span>
        </button>
    );

    return createPortal(
        <div
            ref={ref}
            role="menu"
            aria-label="Eintrag-Aktionen"
            onContextMenu={(e) => e.preventDefault()}
            style={{
                position: "fixed",
                top: pos?.top ?? -9999,
                left: pos?.left ?? -9999,
                visibility: pos ? "visible" : "hidden",
                display: "flex",
                flexDirection: "column",
                minWidth: 236,
                maxWidth: 320,
                padding: 4,
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: 8,
                boxShadow: "0 8px 28px rgba(36, 21, 34, 0.18)",
                zIndex: 100000,
                fontSize: 13,
                color: INK,
                fontFamily: "'Barlow', 'Segoe UI', sans-serif",
            }}
        >
            {z.vorgangId
                ? punkt("Vorgang öffnen", <Open16Regular aria-hidden />, onVorgangOeffnen)
                : [
                      punkt("Neuer Vorgang", <TaskListSquareLtr16Regular aria-hidden />, onNeuerVorgang),
                      punkt("Einem Vorgang zuordnen…", <LinkSquare16Regular aria-hidden />, onVorgangZuordnen),
                  ]}
            {punkt("Kontakt zuweisen…", <PersonAdd16Regular aria-hidden />, onKontaktZuweisen)}
            {z.hatPdf && punkt("Beim Kunden ablegen…", <FolderArrowRight16Regular aria-hidden />, onBeimKundenAblegen)}
            <Trenner />
            {archiviert
                ? punkt("In den Posteingang", <ArrowUndo16Regular aria-hidden />, onArchivUmschalten)
                : punkt("Archivieren", <Archive16Regular aria-hidden />, onArchivUmschalten)}
            <Trenner />
            {z.gelesen
                ? punkt("Als ungelesen markieren", <Mail16Regular aria-hidden />, onGelesenUmschalten)
                : punkt("Als gelesen markieren", <MailRead16Regular aria-hidden />, onGelesenUmschalten)}
        </div>,
        document.body,
    );
};
